// Page templates (cloud ADR 0393, TOM-665; VENDORED.md change 11; the
// contract is cloud/specs/board-pages-v2.md §8). Every board item becomes a
// version 2 page our code writes from its template: static HTML and CSS whose
// figures come through the bindings of §4, drawn as today's item is drawn.
// The writers (the cloud's neoBoardItemAsPage) call makePageTemplate; Neo
// changes the page afterwards like any other.
//
// Pure and deterministic, with no DOM: the same source and context give the
// same output byte for byte, and the server imports this through the core
// entry.

import { definitions } from "../registry.js";
import { BASE_CSS } from "./kit.js";
import { chartTrend } from "./chart-trend.js";
import { chartReadouts } from "./chart-readouts.js";
import { chartBars } from "./chart-bars.js";
import { cardWater } from "./card-water.js";
import { cardMode } from "./card-mode.js";
import { cardAnchor } from "./card-anchor.js";
import { cardDiagram } from "./card-diagram.js";
import { hostedCard, hostedModule } from "./hosted.js";
import { INSTRUMENT_WIDGETS, instrumentTemplate } from "./instrument.js";
import { MODULE_TEMPLATE_PORTS, moduleTemplate } from "./modules.js";
import { controlTemplate } from "./control.js";

export { MODULE_TEMPLATE_PORTS } from "./modules.js";
export { CONTROL_TEMPLATE_PORTS } from "./control.js";
export { defaultScaleFor } from "./kit.js";
export {
  MODULE_DOMAINS,
  PAGE_DOMAINS,
  PAGE_DOMAIN_RULES,
  pageDomainFor,
  pageDomainForModule,
} from "./domains.js";

/** Every overview module kind (the protocol's OVERVIEW_MODULE_KINDS). */
const MODULE_KINDS = [
  "anchor-watch",
  "anchor-details",
  "environment-outlook",
  "forecast",
  "tide",
  "pressure",
  "power-budget",
  "tanks-budget",
  "nearby-vessels",
  "stereo",
  "helm",
  "depth-safety",
  "changes",
  "engine-vitals",
  "reserves",
  "traffic",
  "passage",
  "set-drift",
  "route-map",
  "camera",
];

const CHART_KINDS = ["trend", "readouts", "bars"];
const CARD_KINDS = [
  "water",
  "mode",
  "anchor",
  "map",
  "how-to",
  "friend",
  "diagram",
];
const CONTROL_IDS = definitions.map((d) => d.id).sort();

/** Every template id, sorted: the charts, cards, instruments, modules and controls. */
export const PAGE_TEMPLATE_IDS = Object.freeze(
  [
    ...CHART_KINDS.map((k) => `chart.${k}`),
    ...CARD_KINDS.map((k) => `card.${k}`),
    ...INSTRUMENT_WIDGETS.map((w) => `instrument.${w}`),
    ...MODULE_KINDS.map((k) => `module.${k}`),
    ...CONTROL_IDS.map((id) => `control.${id}`),
  ].sort(),
);

/**
 * Each template's version, by id: 1 at first. A change to a template's output
 * bumps its entry, and the generator writes an unedited page again when its
 * mark's version is older (§11.3).
 */
/**
 * The templates whose output changed since their first version (TOM-665,
 * after Mike's review on Gallivant's boards):
 * - card.diagram 2: box names and reading names no longer cut short where
 *   they fit, and the drawing no larger than the board drew it, so its
 *   legend shows.
 * - card.water 2: a tank with no level draws today's dashed empty lane and
 *   no unit after its "—", and a card with no hero starts at its attention
 *   line.
 * - chart.trend 2, instrument.chart 2: lines draw what the window holds
 *   whatever the reading now, and an empty window keeps its frame, time
 *   axis and a plain grid with "No data in this window" (Mike, 2026-10-14).
 * - instrument.tile, gauge, dial, level and sparkline 2: with no reading
 *   the face stays, the unit after the muted "—" included, and a sparkline
 *   whose window holds nothing draws a dashed rule (Mike, 2026-10-14).
 * - instrument.gauge and dial 3: the value fits inside the arc or the disc,
 *   smaller as its text is longer, never under 14 px, its unit below it
 *   when it still does not fit (Mike, 2026-10-14).
 *
 * The Marina look (cloud ADR 0396, TOM-678; VENDORED.md change 15), one more
 * each: every template that draws a reading takes its kind's colour
 * (`data-domain`, domains.js) for its line, area, bar, needle and marks, the
 * area fading from the kind's top opacity to its foot, 700 numerals with
 * their units in ink, the "▲ 24H" range chip over a stored 24 h history, a
 * warning as its mark and words (a bar keeps its kind's colour), and the
 * diagram's role tints from tokens. The mode and anchor cards, of no kind,
 * take the 700 numerals and the ink unit. The hosted pages and the control
 * faces' pages are unchanged; a face takes its kind's colour in the drawer
 * (page.js, render.js).
 *
 * - chart.trend 4 (TOM-953): a trend of two units draws the second on a
 *   right axis, its lines and numbers in its kind's colour, the units over
 *   both axes and in the legend, and its first reading's chip statistic
 *   under the chip. A trend of one unit is written byte for byte as in 3;
 *   the bump writes unedited pages again, so a board item that holds a
 *   second unit is drawn with its right axis.
 * - instrument.chart, dial, gauge, level, sparkline and tile, one more each
 *   (TOM-989): the card's freshness line is the page's own, a
 *   `data-tc-freshness` line at its foot, so Neo can change it. It says
 *   what the app's line under the card said, and nothing while live.
 * - chart.trend one more (TOM-1001): day bands shade the window's first day
 *   and every other, as HistoryChart does (they shaded the second), for
 *   windows of 40 hours to 31 days, every day of them (they stopped after
 *   the fourteenth); a longer window has none.
 * - instrument.sparkline 5 (TOM-1000): the trend's inset is 4 px all round.
 *   The 16 px top and bottom left a small sparkline's 24 px drawing a plot
 *   −8 px tall, so its trend drew upside down with its area above it; at m
 *   the trend now uses 44 px of its 52, and its area reaches 4 px from the
 *   foot.
 * - One more each (TOM-1001): chart.trend and instrument.chart draw each
 *   legend key as a sample of its line (the dashed key had painted solid),
 *   and name the plot's size once; chart.readouts keeps its trend's area
 *   (`fill: none` had hit it) and draws a trend whose window holds history
 *   with its reading missing, greyed; chart.bars draws a partial latest bar
 *   as one colour, no text in a bar, "No data in this window" for an empty
 *   window, and thins values and labels on a narrow card; instrument.dial
 *   and instrument.level paint nothing in the card's colour (the dial's
 *   disc, the level's quarter marks), which the night tint had turned into
 *   dark holes.
 * - module.tide one more (TOM-1001): its turns' dots and labels take single-
 *   class rules, which reach the copies their <use> draws; a descendant rule
 *   did not, and they drew in the default black.
 */
const TEMPLATE_VERSION_BUMPS = Object.freeze({
  "card.anchor": 2,
  "card.diagram": 3,
  "card.mode": 2,
  "card.water": 3,
  "chart.bars": 3,
  "chart.readouts": 3,
  "chart.trend": 6,
  "instrument.chart": 5,
  "instrument.dial": 6,
  "instrument.gauge": 5,
  "instrument.level": 5,
  "instrument.sparkline": 5,
  "instrument.tile": 4,
  "module.depth-safety": 2,
  "module.engine-vitals": 2,
  "module.environment-outlook": 2,
  "module.forecast": 2,
  "module.helm": 2,
  "module.power-budget": 2,
  "module.pressure": 2,
  "module.reserves": 2,
  "module.tanks-budget": 2,
  "module.tide": 3,
  "module.traffic": 2,
});

export const PAGE_TEMPLATE_VERSIONS = Object.freeze(
  Object.fromEntries(
    PAGE_TEMPLATE_IDS.map((id) => [id, TEMPLATE_VERSION_BUMPS[id] ?? 1]),
  ),
);

const isRecord = (v) =>
  v !== null && typeof v === "object" && !Array.isArray(v);

/**
 * The template a source item takes, or null: a page, a picture, a widget no
 * template draws, a control this library does not draw, a kind it does not
 * know.
 */
export function pageTemplateIdFor(source) {
  const spec = source?.spec;
  if (!isRecord(spec) || spec.v !== 1 || typeof spec.kind !== "string")
    return null;
  if (CHART_KINDS.includes(spec.kind)) return `chart.${spec.kind}`;
  if (CARD_KINDS.includes(spec.kind)) return `card.${spec.kind}`;
  if (spec.kind === "instrument")
    return INSTRUMENT_WIDGETS.includes(spec.widget)
      ? `instrument.${spec.widget}`
      : null;
  if (spec.kind === "module")
    return MODULE_KINDS.includes(spec.module) ? `module.${spec.module}` : null;
  if (spec.kind === "control")
    return CONTROL_IDS.includes(spec.control)
      ? `control.${spec.control}`
      : null;
  return null;
}

function draw(id, source, context) {
  const spec = source.spec;
  switch (id) {
    case "chart.trend":
      return chartTrend(source, context);
    case "chart.readouts":
      return chartReadouts(source, context);
    case "chart.bars":
      return chartBars(source, context);
    case "card.water":
      return cardWater(source, context);
    case "card.mode":
      return cardMode(source, context);
    case "card.anchor":
      return cardAnchor(source, context);
    case "card.diagram":
      return cardDiagram(source, context);
    case "card.map":
    case "card.how-to":
    case "card.friend":
      return hostedCard(id, source);
  }
  if (spec.kind === "instrument") return instrumentTemplate(source, context);
  if (spec.kind === "module")
    return MODULE_TEMPLATE_PORTS.includes(spec.module)
      ? moduleTemplate(source, context)
      : hostedModule(source);
  return controlTemplate(source, context);
}

/**
 * The page for one source item (§8.2): pure and deterministic, byte for
 * byte. Throws for a source pageTemplateIdFor refuses.
 */
export function makePageTemplate(source, context = { paths: {} }) {
  const id = pageTemplateIdFor(source);
  if (id === null)
    throw new Error(
      `No template draws a ${String(source?.spec?.kind ?? "source")} item`,
    );
  const out = draw(id, source, context ?? { paths: {} });
  // Every template but a control's face page starts from the card body's
  // base; a face page is exactly the panel's page (pageFromPanel), no CSS.
  const css = out.bare === true ? out.css : `${BASE_CSS}${out.css}`;
  return {
    id,
    version: PAGE_TEMPLATE_VERSIONS[id] ?? 1,
    html: out.html,
    css,
    readings: out.readings,
    live: out.live,
    feeds: out.feeds,
    card: out.card,
  };
}
